import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { User, AdminPermission } from '../../types';
import { 
    Search, Mail, Trophy, Clock, X, Zap, 
    AlertTriangle, ShieldCheck, Activity as ActivityIcon, Users, 
    Filter, RefreshCcw, UserCheck, Key, Shield,
    UserPlus, Trash2, Edit, Save, CheckCircle,
    LayoutDashboard, BookCheck, Layers, Sparkles,
    Terminal, FileQuestion, Megaphone, Settings,
    Globe, Lock, Star, ChevronRight, Target, Unlock,
    Ban, ShieldAlert, CircleUser, Eye, EyeOff, Loader2,
    MapPin, Wifi, WifiOff, CalendarDays, ClipboardList,
    History as HistoryIcon, CreditCard, ChevronLeft,
    CheckSquare, Square, Download, ArrowUpDown, ChevronDown,
    ArrowUp, ArrowDown, ExternalLink, FilterX
} from 'lucide-react';
import { StorageService } from '../../services/storageService';
import { useAuth } from '../../components/AuthBridge';
import { PageLoader } from '../../components/PageLoader';
import { US_STATES } from '../../data/mockData';
import { TwoFactorSecurityModal } from '../../components/admin/TwoFactorSecurityModal';

const PERMISSION_MANIFEST: { id: AdminPermission; label: string; icon: any; color: string }[] = [
    { id: 'admin.dashboard', label: 'Overview Metrics', icon: LayoutDashboard, color: 'bg-[#0F7D6C]' },
    { id: 'admin.study-content', label: 'Study Deck Editor', icon: BookCheck, color: 'bg-medical-500' },
    { id: 'admin.skills-content', label: 'Skills Lab Editor', icon: Layers, color: 'bg-emerald-500' },
    { id: 'admin.factory', label: 'AI Content Factory', icon: Sparkles, color: 'bg-indigo-500' },
    { id: 'admin.prompts', label: 'AI Prompt Engineering', icon: Terminal, color: 'bg-slate-700' },
    { id: 'admin.questions', label: 'Question Bank Access', icon: FileQuestion, color: 'bg-amber-500' },
    { id: 'admin.scenarios', label: 'Scenarios & Challenges', icon: Target, color: 'bg-orange-500' },
    { id: 'admin.simulator-lab', label: 'Simulator Lab', icon: Zap, color: 'bg-yellow-500' },
    { id: 'admin.flashcards', label: 'Flashcards Master', icon: Star, color: 'bg-amber-600' },
    { id: 'admin.podcasts', label: 'Podcast Publishing', icon: Megaphone, color: 'bg-violet-500' },
    { id: 'admin.certifications', label: 'Certification States', icon: Globe, color: 'bg-sky-500' },
    { id: 'admin.promos', label: 'Marketing Promos', icon: Zap, color: 'bg-rose-400' },
    { id: 'admin.sponsored', label: 'Branding & Ads', icon: Megaphone, color: 'bg-rose-500' },
    { id: 'admin.users', label: 'Staff & Roles', icon: Users, color: 'bg-purple-500' },
    { id: 'admin.config', label: 'Exam Setup', icon: Settings, color: 'bg-cyan-500' },
    { id: 'admin.ai-status', label: 'AI Health Monitor', icon: ActivityIcon, color: 'bg-emerald-600' },
    { id: 'admin.database', label: 'Database & Backups', icon: Shield, color: 'bg-red-600' },
    { id: 'admin.feedback', label: 'User Feedback', icon: Mail, color: 'bg-teal-500' },
];

interface UserManagerProps {
    autoOpen2FA?: boolean;
}

export const UserManager: React.FC<UserManagerProps> = ({ autoOpen2FA = false }) => {
    const { user: currentUser, updateUser: updateActiveSession } = useAuth();
    
    // Grid State
    const [users, setUsers] = useState<User[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [isLoading, setIsLoading] = useState(true);
    const [view, setView] = useState<'students' | 'staff'>('students');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    
    // Advanced Filters
    const [showFilters, setShowFilters] = useState(false);
    const [tierFilter, setTierFilter] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    
    // Sorting
    const [sortBy, setSortBy] = useState('lastActive');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    
    // UI Options
    const [compactMode, setCompactMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    
    // Interaction States
    const [selectedEntity, setSelectedEntity] = useState<User | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditingUser, setIsEditingUser] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [status, setStatus] = useState<{type: 'error' | 'success', text: string} | null>(null);
    const [detailTab, setDetailTab] = useState<'profile' | 'history' | 'plan' | 'sessionLog'>('profile');
    const [sessionTrace, setSessionTrace] = useState<any>(null);
    const [isTraceLoading, setIsTraceLoading] = useState(false);
    const [show2FAModal, setShow2FAModal] = useState(autoOpen2FA);
    const [toggling2FAUserId, setToggling2FAUserId] = useState<string | null>(null);
    
    const [userForm, setUserForm] = useState<Partial<User>>({
        name: '', email: '', password: '', role: 'student', permissions: ['admin.dashboard'], isBlocked: false, planTier: 'free', twoFactorEnabled: false
    });

    const isSuperAdmin = currentUser?.permissions?.includes('*');

    const handleToggleUser2FA = async (targetUser: User) => {
        if (!targetUser || !targetUser.id) return;
        const nextState = !targetUser.twoFactorEnabled;
        const confirmMsg = nextState 
            ? `Turn ON Two-Factor Authentication for ${targetUser.name || targetUser.email}?\n\nThis Account Architect will be required to use Google Authenticator TOTP verification.`
            : `Turn OFF Two-Factor Authentication for ${targetUser.name || targetUser.email}?\n\nThis will remove 2FA enforcement, reset their secret key, and revoke all remembered devices for this account.`;
            
        if (!confirm(confirmMsg)) return;

        setToggling2FAUserId(targetUser.id);
        try {
            const res = await StorageService.adminToggleUser2FA(targetUser.id, nextState);
            setStatus({ 
                type: 'success', 
                text: res.message || `2FA successfully ${nextState ? 'enabled' : 'disabled'} for ${targetUser.name || targetUser.email}.` 
            });
            
            // Update local state in user list
            setUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, twoFactorEnabled: nextState } : u));
            
            // Update selectedEntity if open
            if (selectedEntity && selectedEntity.id === targetUser.id) {
                setSelectedEntity(prev => prev ? { ...prev, twoFactorEnabled: nextState } : null);
            }
            
            // If modifying current logged-in user, update session
            if (currentUser && (currentUser.id === targetUser.id || currentUser.email === targetUser.email)) {
                updateActiveSession({ ...currentUser, twoFactorEnabled: nextState });
            }
        } catch (err: any) {
            setStatus({ type: 'error', text: err.message || 'Failed to update 2FA configuration.' });
        } finally {
            setToggling2FAUserId(null);
        }
    };

    // Fetch first 10m session activity trace
    useEffect(() => {
        if (selectedEntity && detailTab === 'sessionLog') {
            setIsTraceLoading(true);
            setSessionTrace(null);
            StorageService.getUserSessionActivityLog(selectedEntity.id || selectedEntity.email)
                .then(res => {
                    setSessionTrace(res);
                })
                .catch(err => {
                    console.error("Failed to load session trace", err);
                })
                .finally(() => {
                    setIsTraceLoading(false);
                });
        }
    }, [selectedEntity, detailTab]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await StorageService.getGlobalUsers({
                page,
                limit,
                search: debouncedSearch,
                role: view === 'staff' ? 'admin' : 'student',
                planTier: tierFilter,
                state: stateFilter,
                isBlocked: statusFilter === '' ? '' : statusFilter === 'suspended',
                sortBy,
                sortOrder
            });
            // FIX: Defensive array check
            setUsers(result.items || []);
            setTotal(result.total || 0);
        } catch (error) {
            console.error("Registry Hydration Error:", error);
            setStatus({ type: 'error', text: 'Registry sync error: Node unreachable.' });
            setUsers([]);
        } finally {
            setIsLoading(false);
        }
    }, [page, limit, debouncedSearch, view, tierFilter, stateFilter, statusFilter, sortBy, sortOrder]);

    useEffect(() => { 
        loadData();
    }, [loadData]);

    /**
     * Fix: Implement handleEditUser to populate the form and open the editor modal.
     */
    const handleEditUser = (user: User) => {
        const role = user.permissions?.includes('*') ? 'superadmin' : (user.role || 'student');
        setUserForm({
            id: user.id,
            name: user.name || '',
            email: user.email || '',
            role: role,
            planTier: user.planTier || 'free',
            permissions: user.permissions || [],
            isBlocked: !!user.isBlocked,
            state: user.state || 'Global',
            twoFactorEnabled: !!user.twoFactorEnabled,
            createdAt: user.createdAt
        });
        setIsEditingUser(true);
        setShowPass(false);
        setStatus(null);
    };

    /**
     * Fix: Implement toggleBlockStatus to update user suspension status in the registry.
     */
    const toggleBlockStatus = async (user: User) => {
        if (user.id === currentUser?.id) return;
        setIsLoading(true);
        try {
            const updated = await StorageService.saveUser({ ...user, isBlocked: !user.isBlocked });
            setStatus({ type: 'success', text: `User ${updated.isBlocked ? 'suspended' : 'restored'} in registry.` });
            loadData();
            if (selectedEntity?.id === user.id) {
                setSelectedEntity(updated);
            }
        } catch (e) {
            setStatus({ type: 'error', text: 'Status update failure.' });
        } finally {
            setIsLoading(false);
        }
    };

    const isUserOnline = (lastActive?: string | Date) => {
        if (!lastActive) return false;
        const last = new Date(lastActive).getTime();
        const now = Date.now();
        return (now - last) < 150000;
    };

    const getRelativeTime = (date?: string | Date) => {
        if (!date) return 'Never';
        const now = Date.now();
        const past = new Date(date).getTime();
        const diff = Math.floor((now - past) / 1000);
        if (diff < 60) return 'Just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return new Date(date).toLocaleDateString();
    };

    const handleSort = (key: string) => {
        if (sortBy === key) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortOrder('desc');
        }
        setPage(1);
    };

    const handleSaveUser = async () => {
        if (!userForm.name || !userForm.email) {
            setStatus({ type: 'error', text: 'Identity violation: Identifier fields are mandatory.' });
            return;
        }

        const isStaffOrAdmin = userForm.role === 'admin' || userForm.role === 'superadmin';
        if (!userForm.id && isStaffOrAdmin && (!userForm.password || !userForm.password.trim())) {
            setStatus({ type: 'error', text: 'Access code/password is mandatory when creating Super Admin or Staff accounts.' });
            return;
        }

        setIsSaving(true);
        setStatus(null);
        
        try {
            const payload: any = {
                ...userForm,
                id: userForm.id || `u_${Date.now()}`,
                email: userForm.email.toLowerCase().trim(),
                role: (userForm.role === 'superadmin' ? 'admin' : userForm.role) || 'student',
                planTier: userForm.planTier || 'free',
                permissions: userForm.role === 'superadmin' ? ['*'] : (userForm.role === 'admin' ? (userForm.permissions || []) : []),
                isBlocked: !!userForm.isBlocked,
                twoFactorEnabled: !!userForm.twoFactorEnabled,
                createdAt: userForm.createdAt || new Date().toISOString()
            };

            if (!userForm.password || !userForm.password.trim()) {
                delete payload.password;
            } else {
                payload.password = userForm.password.trim();
            }
            
            const result = await StorageService.saveUser(payload as User);
            if (result && result.email === currentUser?.email) {
                updateActiveSession(result);
            }

            setStatus({ type: 'success', text: 'Registry synchronized.' });
            setIsEditingUser(false);
            loadData();
            if (result && selectedEntity?.id === result.id) setSelectedEntity(result); 
        } catch (e: any) {
            setStatus({ type: 'error', text: e.message || 'Synchronization failed.' });
        } finally {
            setIsSaving(false);
        }
    };

    const toggleSelectAll = () => {
        if (!Array.isArray(users)) return;
        if (selectedIds.size === users.length) setSelectedIds(new Set());
        else setSelectedIds(new Set(users.map(u => u.id)));
    };

    const toggleSelect = (id: string) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedIds(next);
    };

    const handleBulkSuspend = async () => {
        if (selectedIds.size === 0) return;
        if (!confirm(`Executing Bulk Suspension on ${selectedIds.size} records. Proceed?`)) return;
        setIsLoading(true);
        try {
            for (const id of selectedIds) {
                const user = users.find(u => u.id === id);
                if (user && user.id !== currentUser?.id) {
                    await StorageService.saveUser({ ...user, isBlocked: true });
                }
            }
            setSelectedIds(new Set());
            loadData();
            setStatus({ type: 'success', text: 'Bulk registry modification successful.' });
        } catch (e) {
            setStatus({ type: 'error', text: 'Bulk operation failed.' });
        }
    };

    const handleBulkExport = () => {
        const selectedUsers = users.filter(u => selectedIds.has(u.id));
        const dataStr = JSON.stringify(selectedUsers, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', `registry_export_${Date.now()}.json`);
        linkElement.click();
    };

    const getAggregatedScore = (email?: string) => {
        if (!email) return 0;
        const p = StorageService.getProgress(email);
        const history = p.history || [];
        return history.length > 0 ? Math.round(history.reduce((a,b) => a+b.score, 0)/history.length) : 0;
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="flex flex-col h-full animate-fade-in pb-12 relative text-slate-900">
            
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-8 gap-8 px-4 md:px-0">
                <div>
                    <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight flex items-center gap-4">
                        <Users className="text-medical-600" size={40} /> Registry Grid
                    </h2>
                    <p className="text-slate-500 font-medium mt-1">Authorized access to the clinical identity namespace. Total depth: {total} records.</p>
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex bg-white p-1 rounded-2xl border border-slate-200 shadow-sm">
                        <button onClick={() => { setView('students'); setPage(1); }} className={`px-8 py-3 rounded-xl text-[10px] font-black tracking-widest transition-all ${view === 'students' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}>Candidates</button>
                        <button onClick={() => { setView('staff'); setPage(1); }} className={`px-8 py-3 rounded-xl text-[10px] font-black tracking-widest transition-all ${view === 'staff' ? 'bg-medical-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}>Staff</button>
                    </div>
                    {isSuperAdmin && (
                        <button 
                            onClick={() => {
                                setUserForm({ name: '', email: '', password: '', role: view === 'staff' ? 'admin' : 'student', permissions: ['admin.dashboard'], isBlocked: false, planTier: 'free', state: 'Global' });
                                setIsEditingUser(true);
                                setShowPass(false);
                                setStatus(null);
                            }}
                            className={`p-4 ${view === 'staff' ? 'bg-medical-600 shadow-medical-500/20' : 'bg-slate-900 shadow-slate-900/20'} text-white rounded-2xl transition-all shadow-xl active:scale-95`}
                        >
                            <UserPlus size={22} />
                        </button>
                    )}
                </div>
            </div>

            {status && (
                <div className="px-4 md:px-0">
                    <div className={`p-4 rounded-3xl mb-6 flex items-center justify-between animate-pop shadow-xl border ${status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}`}>
                        <div className="flex items-center gap-3">
                            {status.type === 'error' ? <AlertTriangle size={18}/> : <CheckCircle size={18}/>}
                            <span className="font-black text-xs tracking-widest">{status.text}</span>
                        </div>
                        <button onClick={() => setStatus(null)} className="p-1 hover:bg-black/5 rounded-lg"><X size={14}/></button>
                    </div>
                </div>
            )}

            <div className="flex-1 flex flex-col min-h-0 bg-white rounded-[2.5rem] shadow-sm border border-slate-200 overflow-hidden mx-4 md:mx-0">
                {/* TOOLBAR */}
                <div className="p-4 border-b border-slate-50 flex flex-col gap-4 bg-slate-50/20">
                    <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                        <div className="relative flex-1 w-full group">
                            <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-medical-500 transition-colors" size={20} />
                            <input 
                                type="text" 
                                placeholder={`Search Identity / Email / ID...`}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-14 pr-6 py-4 bg-white border border-slate-200 rounded-2xl outline-none focus:ring-4 focus:ring-medical-500/10 font-bold text-slate-800 shadow-inner"
                            />
                        </div>
                        
                        <div className="flex items-center gap-2 shrink-0">
                            <button 
                                onClick={() => setShowFilters(!showFilters)}
                                className={`p-4 rounded-2xl transition-all border ${showFilters ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-400 hover:text-slate-600'}`}
                            >
                                <Filter size={20} />
                            </button>
                            <button 
                                onClick={() => setCompactMode(!compactMode)}
                                className={`p-4 rounded-2xl transition-all border ${compactMode ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-400'}`}
                                title="Toggle High-Density Mode"
                            >
                                <LayoutDashboard size={20} />
                            </button>
                            <div className="w-px h-8 bg-slate-200 mx-1"></div>
                            <button onClick={loadData} className="p-4 text-slate-400 hover:text-indigo-600 transition-all active:scale-90"><RefreshCcw size={20} className={isLoading ? 'animate-spin' : ''}/></button>
                        </div>
                    </div>

                    {/* COLLAPSIBLE FILTERS */}
                    {showFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 animate-slide-up pt-2">
                            <div className="relative">
                                <Star className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                                <select value={tierFilter} onChange={e => setTierFilter(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl font-black text-[9px] outline-none focus:border-indigo-400">
                                    <option value="">All Tiers</option>
                                    <option value="free">Standard</option>
                                    <option value="paid">Elite Pro</option>
                                </select>
                            </div>
                            <div className="relative">
                                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                                <select value={stateFilter} onChange={e => setStateFilter(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl font-black text-[9px] outline-none focus:border-indigo-400">
                                    <option value="">All States</option>
                                    {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                                </select>
                            </div>
                            <div className="relative">
                                <ShieldAlert className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
                                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl font-black text-[9px] outline-none focus:border-indigo-400">
                                    <option value="">All Statuses</option>
                                    <option value="operational">Operational</option>
                                    <option value="suspended">Suspended</option>
                                </select>
                            </div>
                            <button 
                                onClick={() => { setTierFilter(''); setStateFilter(''); setStatusFilter(''); setSearchTerm(''); }}
                                className="flex items-center justify-center gap-2 px-6 py-2.5 bg-slate-100 text-slate-500 rounded-xl font-black text-[9px] hover:bg-slate-200 transition-all"
                            >
                                <FilterX size={14}/> Clear Registry Filters
                            </button>
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-x-auto custom-scrollbar relative">
                    <table className="w-full text-left border-collapse min-w-[900px]">
                        <thead className="bg-slate-50 sticky top-0 z-20">
                            <tr>
                                <th className="py-4 px-6 w-12 text-center">
                                    <button onClick={toggleSelectAll} className={`p-1.5 rounded-lg transition-colors ${selectedIds.size === (users?.length || 0) && (users?.length || 0) > 0 ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-400'}`}>
                                        {selectedIds.size === (users?.length || 0) && (users?.length || 0) > 0 ? <CheckSquare size={14}/> : <Square size={14}/>}
                                    </button>
                                </th>
                                <th onClick={() => handleSort('name')} className="py-4 px-6 text-[9px] font-black text-slate-400 tracking-[0.2em] cursor-pointer group">
                                    <div className="flex items-center gap-2">
                                        Identity {sortBy === 'name' ? (sortOrder === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-0 group-hover:opacity-40" />}
                                    </div>
                                </th>
                                <th className="py-4 px-4 text-[9px] font-black text-slate-400 tracking-[0.2em] text-center">Tier</th>
                                <th className="py-4 px-4 text-[9px] font-black text-slate-400 tracking-[0.2em] text-center">Jurisdiction</th>
                                <th onClick={() => handleSort('createdAt')} className="py-4 px-4 text-[9px] font-black text-slate-400 tracking-[0.2em] text-center cursor-pointer group">
                                    <div className="flex items-center justify-center gap-2">
                                        Joined {sortBy === 'createdAt' ? (sortOrder === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-0 group-hover:opacity-40" />}
                                    </div>
                                </th>
                                <th onClick={() => handleSort('lastActive')} className="py-4 px-6 text-[9px] font-black text-slate-400 tracking-[0.2em] text-center cursor-pointer group">
                                    <div className="flex items-center justify-center gap-2">
                                        Last Active {sortBy === 'lastActive' ? (sortOrder === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowUpDown size={10} className="opacity-0 group-hover:opacity-40" />}
                                    </div>
                                </th>
                                <th className="py-4 px-8 text-right text-[9px] font-black text-slate-400 tracking-[0.2em]">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {isLoading ? (
                                <tr><td colSpan={7} className="py-32 text-center opacity-30 animate-pulse"><Loader2 size={48} className="mx-auto mb-4 animate-spin"/> Hydrating Registry...</td></tr>
                            ) : (!users || users.length === 0) ? (
                                <tr><td colSpan={7} className="py-32 text-center opacity-40"><CircleUser size={64} className="mx-auto mb-4"/> Namespace Empty</td></tr>
                            ) : users.map(entity => {
                                const online = isUserOnline(entity.lastActive);
                                const isWix = entity.id?.length > 20; // Heuristic for UUIDs
                                return (
                                    <tr 
                                        key={entity.id} 
                                        onClick={() => setSelectedEntity(entity)}
                                        className={`group cursor-pointer hover:bg-slate-50 transition-colors ${selectedEntity?.id === entity.id ? 'bg-medical-50/40' : ''}`}
                                    >
                                        <td className="py-4 px-6 text-center" onClick={e => e.stopPropagation()}>
                                            <button onClick={() => toggleSelect(entity.id)} className={`p-1.5 rounded-lg transition-colors ${selectedIds.has(entity.id) ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-300'}`}>
                                                {selectedIds.has(entity.id) ? <CheckSquare size={14}/> : <Square size={14}/>}
                                            </button>
                                        </td>
                                        <td className={`${compactMode ? 'py-3' : 'py-5'} px-6`}>
                                            <div className="flex items-center gap-4">
                                                <div className={`rounded-xl flex items-center justify-center font-black shadow-sm group-hover:scale-110 transition-transform shrink-0 ${compactMode ? 'w-8 h-8 text-[10px]' : 'w-11 h-11 text-base'} ${entity.isBlocked ? 'bg-rose-500 text-white' : view === 'staff' ? 'bg-medical-600 text-white' : 'bg-slate-900 text-white'}`}>
                                                    {entity.isBlocked ? <Ban size={compactMode ? 14 : 18}/> : entity.name?.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className={`font-black text-slate-800 tracking-tight truncate ${compactMode ? 'text-[11px]' : 'text-sm'}`}>{entity.name}</p>
                                                        {isWix && <div className="p-0.5 bg-indigo-50 text-indigo-400 rounded" title="Wix Federated Identity"><Globe size={10}/></div>}
                                                        {view === 'staff' ? (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (isSuperAdmin) {
                                                                        handleToggleUser2FA(entity);
                                                                    }
                                                                }}
                                                                disabled={toggling2FAUserId === entity.id || !isSuperAdmin}
                                                                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider transition-all border ${
                                                                    entity.twoFactorEnabled 
                                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 shadow-xs' 
                                                                        : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'
                                                                } ${isSuperAdmin ? 'cursor-pointer hover:scale-105 active:scale-95' : 'cursor-default'}`}
                                                                title={isSuperAdmin 
                                                                    ? (entity.twoFactorEnabled ? "2FA is ON. Click to turn OFF for this Account Architect" : "2FA is OFF. Click to turn ON for this Account Architect") 
                                                                    : (entity.twoFactorEnabled ? "Google Authenticator 2FA Protected" : "2FA Not Enforced")
                                                                }
                                                            >
                                                                {toggling2FAUserId === entity.id ? (
                                                                    <Loader2 size={9} className="animate-spin text-medical-600" />
                                                                ) : (
                                                                    <ShieldCheck size={10} className={entity.twoFactorEnabled ? "text-emerald-600" : "text-slate-400"} />
                                                                )}
                                                                <span>{entity.twoFactorEnabled ? "2FA ON" : "2FA OFF"}</span>
                                                            </button>
                                                        ) : (
                                                            entity.twoFactorEnabled && (
                                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200" title="Google Authenticator 2FA Protected">
                                                                    <ShieldCheck size={9} /> 2FA
                                                                </span>
                                                            )
                                                        )}
                                                    </div>
                                                    {!compactMode && <p className="text-[10px] text-slate-400 font-bold truncate">{entity.email}</p>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            {entity.planTier === 'paid' ? (
                                                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 rounded text-[8px] font-black">
                                                    <Star size={8} className="fill-current" /> Elite
                                                </div>
                                            ) : (
                                                <span className="text-[8px] font-black text-slate-400 tracking-widest">Free</span>
                                            )}
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-slate-50 border border-slate-100 rounded text-[9px] font-black text-slate-600">
                                                <MapPin size={10} className="text-medical-400" /> {entity.state || 'GLB'}
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            <p className="text-[10px] font-bold text-slate-400 tabular-nums">{new Date(entity.createdAt || '').toLocaleDateString()}</p>
                                        </td>
                                        <td className="py-4 px-6 text-center">
                                            <div className="flex flex-col items-center gap-1">
                                                <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full border transition-all ${online ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                                                    <div className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></div>
                                                    <span className="text-[8px] font-black tracking-widest">{getRelativeTime(entity.lastActive)}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4 px-8 text-right">
                                            <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                                                {view === 'staff' && isSuperAdmin && (
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); handleToggleUser2FA(entity); }}
                                                        disabled={toggling2FAUserId === entity.id}
                                                        className={`p-2 rounded-lg transition-all cursor-pointer ${
                                                            entity.twoFactorEnabled 
                                                                ? 'text-emerald-600 hover:bg-emerald-50' 
                                                                : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
                                                        }`}
                                                        title={entity.twoFactorEnabled ? "Turn OFF 2FA for this Account Architect" : "Turn ON 2FA for this Account Architect"}
                                                        aria-label="Toggle 2FA Settings"
                                                    >
                                                        {toggling2FAUserId === entity.id ? (
                                                            <Loader2 size={16} className="animate-spin text-medical-600" />
                                                        ) : (
                                                            <ShieldCheck size={16} />
                                                        )}
                                                    </button>
                                                )}
                                                <button onClick={(e) => { e.stopPropagation(); handleEditUser(entity); }} className="p-2 text-slate-400 hover:text-indigo-600"><Edit size={16}/></button>
                                                {entity.id !== currentUser?.id && (
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); toggleBlockStatus(entity); }}
                                                        className={`p-2 rounded-lg transition-all ${entity.isBlocked ? 'text-emerald-500' : 'text-rose-500'}`}
                                                    >
                                                        {entity.isBlocked ? <Unlock size={16} /> : <Ban size={16} />}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                <div className="px-6 py-4 border-t border-slate-50 flex items-center justify-between bg-slate-50/30">
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-3">
                            <span className="text-[9px] font-black text-slate-400 tracking-widest">Show</span>
                            <select value={limit} onChange={e => {setLimit(parseInt(e.target.value)); setPage(1);}} className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-black outline-none focus:ring-2 focus:ring-medical-500/20">
                                {[25, 50, 100, 250].map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                        <p className="text-[9px] font-black text-slate-400 tracking-widest">Records {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} of {total}</p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button disabled={page === 1} onClick={() => setPage(page - 1)} className="p-2 rounded-xl bg-white border border-slate-200 text-slate-400 hover:text-slate-800 disabled:opacity-20 transition-all"><ChevronLeft size={20}/></button>
                        <div className="flex gap-1">
                            {/* Simplified Pagination logic */}
                            <div className="px-4 py-2 bg-slate-900 text-white rounded-xl font-black text-[10px] shadow-lg shadow-slate-900/20">Page {page} / {Math.max(1, totalPages)}</div>
                        </div>
                        <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="p-2 rounded-xl bg-white border border-slate-200 text-slate-400 hover:text-slate-800 disabled:opacity-20 transition-all"><ChevronRight size={20}/></button>
                    </div>
                </div>
            </div>

            {/* BULK ACTION BAR */}
            {selectedIds.size > 0 && (
                <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[200] w-fit animate-slide-up">
                    <div className="bg-slate-900 text-white rounded-3xl p-4 shadow-[0_30px_60px_rgba(0,0,0,0.5)] border border-white/10 flex items-center gap-8 ring-8 ring-black/10">
                        <div className="flex items-center gap-3 px-4 py-2 bg-white/10 rounded-2xl">
                            <span className="text-xl font-black">{selectedIds.size}</span>
                            <span className="text-[8px] font-black tracking-[0.2em] text-slate-400">Selected</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={handleBulkSuspend} className="px-6 py-3 bg-rose-600 hover:bg-rose-500 rounded-xl font-black text-[9px] tracking-widest transition-all flex items-center gap-2 border-b-2 border-rose-800 active:scale-95">
                                <Ban size={14}/> Suspend
                            </button>
                            <button onClick={handleBulkExport} className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-black text-[9px] tracking-widest transition-all flex items-center gap-2 border border-white/10 active:scale-95">
                                <Download size={14}/> Export
                            </button>
                        </div>
                        <button onClick={() => setSelectedIds(new Set())} className="p-2 text-slate-500 hover:text-white transition-colors">
                            <X size={20}/>
                        </button>
                    </div>
                </div>
            )}

            {/* Account Provisioning Modal */}
            {isEditingUser && (
                <div className="fixed inset-0 z-[300] bg-slate-950/80 backdrop-blur-xl flex items-start justify-center p-4 pt-10 md:pt-16 overflow-y-auto custom-scrollbar">
                    <div className="bg-white rounded-[3.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-pop overflow-hidden border border-white/10">
                        <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                            <div>
                                <h3 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                                    <UserPlus className="text-medical-500" /> Account Architect
                                </h3>
                                <p className="text-[10px] font-black text-slate-400 tracking-widest mt-1">Registry Provisioning</p>
                            </div>
                            <button onClick={() => setIsEditingUser(false)} className="p-3 bg-white text-slate-400 rounded-2xl hover:text-rose-500 transition-all shadow-sm border border-slate-100 active:scale-90"><X size={24} /></button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-10 custom-scrollbar space-y-12">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Identity Name</label>
                                        <input type="text" value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-800 outline-none focus:bg-white transition-all shadow-inner" placeholder="Full legal name" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Registry Email</label>
                                        <input type="email" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-800 outline-none focus:bg-white transition-all shadow-inner" placeholder="Primary contact" />
                                    </div>
                                    
                                    <div className="space-y-4 pt-4 border-t border-slate-100">
                                        <div className="flex justify-between items-center">
                                            <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2">
                                                <Key size={14} className="text-indigo-500" /> {userForm.id ? 'Assign New Password' : 'Initial Password'}
                                            </label>
                                            <button onClick={() => setShowPass(!showPass)} className="text-[9px] font-black text-indigo-500 flex items-center gap-1 hover:underline">
                                                {showPass ? 'Hide' : 'Show'}
                                            </button>
                                        </div>
                                        <div className="relative">
                                            <input 
                                                type={showPass ? "text" : "password"} 
                                                value={userForm.password} 
                                                onChange={e => setUserForm({...userForm, password: e.target.value})} 
                                                className="w-full pl-6 pr-6 py-4 bg-slate-900 text-emerald-400 border border-slate-800 rounded-2xl font-mono text-sm outline-none focus:ring-4 focus:ring-emerald-500/10 shadow-2xl" 
                                                placeholder={userForm.id ? "Leave empty to keep current" : "Assign access code"} 
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-8 bg-slate-50/50 p-8 rounded-[2.5rem] border border-slate-100 shadow-inner">
                                    <div className="space-y-4">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Governance Role</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            <button 
                                                onClick={() => setUserForm({...userForm, role: 'student', permissions: []})} 
                                                className={`py-4 rounded-xl text-[9px] font-black transition-all border-2 ${userForm.role === 'student' ? 'bg-slate-900 border-slate-900 text-white shadow-lg' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'}`}
                                            >
                                                Candidate
                                            </button>
                                            <button 
                                                onClick={() => setUserForm({...userForm, role: 'admin', permissions: userForm.permissions?.includes('*') ? ['admin.dashboard'] : (userForm.permissions?.length ? userForm.permissions : ['admin.dashboard'])})} 
                                                className={`py-4 rounded-xl text-[9px] font-black transition-all border-2 ${userForm.role === 'admin' ? 'bg-medical-600 border-medical-600 text-white shadow-lg' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'}`}
                                            >
                                                Staff
                                            </button>
                                            <button 
                                                onClick={() => isSuperAdmin && setUserForm({...userForm, role: 'superadmin', permissions: ['*']})} 
                                                className={`py-4 rounded-xl text-[9px] font-black transition-all border-2 ${userForm.role === 'superadmin' ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'} ${!isSuperAdmin ? 'opacity-20 cursor-not-allowed' : ''}`}
                                                disabled={!isSuperAdmin}
                                            >
                                                Super Admin
                                            </button>
                                        </div>
                                    </div>

                                    {(userForm.role === 'admin' || userForm.role === 'superadmin') && (
                                        <div className="space-y-4 animate-fade-in">
                                            <div className="flex items-center justify-between ml-1">
                                                <label className="text-[10px] font-black text-slate-400 tracking-widest">Rule-Based Access (RBAC)</label>
                                                {userForm.role === 'superadmin' && <span className="text-[8px] font-black text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100">Full Root Access</span>}
                                            </div>
                                            
                                            <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto p-2 bg-white rounded-2xl border border-slate-100 custom-scrollbar">
                                                {PERMISSION_MANIFEST.map(perm => {
                                                    const isSelected = userForm.permissions?.includes(perm.id) || userForm.role === 'superadmin';
                                                    return (
                                                        <button
                                                            key={perm.id}
                                                            disabled={userForm.role === 'superadmin'}
                                                            onClick={() => {
                                                                const current = userForm.permissions || [];
                                                                const next = current.includes(perm.id) 
                                                                    ? current.filter(p => p !== perm.id)
                                                                    : [...current, perm.id];
                                                                setUserForm({ ...userForm, permissions: next });
                                                            }}
                                                            className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left group ${
                                                                isSelected 
                                                                    ? 'bg-slate-900 border-slate-900 text-white shadow-md' 
                                                                    : 'bg-slate-50 border-slate-100 text-slate-400 hover:border-slate-200'
                                                            }`}
                                                        >
                                                            <div className={`p-2 rounded-lg shrink-0 ${isSelected ? 'bg-white/10' : 'bg-white border border-slate-100 shadow-sm'}`}>
                                                                <perm.icon size={14} className={isSelected ? 'text-white' : 'text-slate-400'} />
                                                            </div>
                                                            <span className="text-[9px] font-black tracking-tight leading-tight">{perm.label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-4">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Service Tier</label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <button 
                                                onClick={() => setUserForm({...userForm, planTier: 'free'})} 
                                                className={`py-4 rounded-2xl text-[10px] font-black transition-all border-2 flex items-center justify-center gap-2 ${userForm.planTier === 'free' ? 'bg-slate-700 border-slate-800 text-white shadow-lg' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'}`}
                                            >
                                                <Zap size={14} className={userForm.planTier === 'free' ? 'text-white' : 'text-slate-300'} /> Standard
                                            </button>
                                            <button 
                                                onClick={() => setUserForm({...userForm, planTier: 'paid'})} 
                                                className={`py-4 rounded-2xl text-[10px] font-black transition-all border-2 flex items-center justify-center gap-2 ${userForm.planTier === 'paid' ? 'bg-gradient-to-r from-amber-500 to-amber-600 border-amber-700 text-white shadow-lg' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'}`}
                                            >
                                                <Star size={14} className={userForm.planTier === 'paid' ? 'text-white' : 'text-slate-300'} /> Elite Pro
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Active Jurisdiction</label>
                                        <select 
                                            value={userForm.state || 'Global'} 
                                            onChange={e => setUserForm({...userForm, state: e.target.value})}
                                            className="w-full p-4 bg-white border border-slate-100 rounded-2xl font-bold text-[11px] outline-none focus:ring-2 focus:ring-medical-500/20"
                                        >
                                            <option value="Global">Global Standard</option>
                                            {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                                        </select>
                                    </div>

                                    <div className="space-y-3 bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-xl ${userForm.twoFactorEnabled ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                                                    <ShieldCheck size={18} />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">2FA Security Access</label>
                                                    <p className="text-xs font-bold text-slate-800">
                                                        {userForm.twoFactorEnabled ? '2FA Enforced (Google Authenticator)' : '2FA Inactive (Password Only)'}
                                                    </p>
                                                </div>
                                            </div>
                                            {isSuperAdmin && (
                                                <button
                                                    type="button"
                                                    onClick={() => setUserForm({ ...userForm, twoFactorEnabled: !userForm.twoFactorEnabled })}
                                                    className={`px-3.5 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all border cursor-pointer ${
                                                        userForm.twoFactorEnabled 
                                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100' 
                                                            : 'bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-200'
                                                    }`}
                                                >
                                                    {userForm.twoFactorEnabled ? '2FA: ON' : '2FA: OFF'}
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-[9px] text-slate-400 font-medium">
                                            Super Admin can turn on or off Two-Factor Authentication for this individual Account Architect.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-8 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-4 shrink-0">
                            <button onClick={() => setIsEditingUser(false)} className="px-10 py-4 font-black text-[10px] text-slate-400 hover:text-slate-900 transition-colors">Discard</button>
                            <button 
                                onClick={handleSaveUser} 
                                disabled={isSaving}
                                className="px-14 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] tracking-widest shadow-2xl hover:bg-slate-800 transition-all flex items-center gap-3 disabled:opacity-50 border-b-4 border-black"
                            >
                                {isSaving ? <Loader2 className="animate-spin" size={18}/> : <ShieldCheck size={18} />} 
                                Publish Update
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick View Side Drawer */}
            {selectedEntity && !isEditingUser && (
                <div className="fixed right-0 top-0 h-screen w-full xl:w-[600px] bg-white border-l border-slate-200 shadow-2xl z-[400] flex flex-col animate-slide-in-right overflow-hidden">
                    <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/30 shrink-0">
                        <div className="flex items-center gap-5">
                            <div className={`w-14 h-14 rounded-2xl bg-slate-900 text-white flex items-center justify-center font-black text-xl shadow-lg`}>
                                {selectedEntity.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <h3 className="text-2xl font-black text-slate-900 tracking-tight leading-none">{selectedEntity.name}</h3>
                                <div className="flex items-center gap-3 mt-2">
                                    <span className="text-[10px] font-black text-slate-400 tracking-widest">Registry Profile Detail</span>
                                    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${isUserOnline(selectedEntity.lastActive) ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                                        <div className={`w-1.5 h-1.5 rounded-full ${isUserOnline(selectedEntity.lastActive) ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></div>
                                        <span className="text-[7px] font-black tracking-tighter">{isUserOnline(selectedEntity.lastActive) ? 'Live' : 'Cached'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button onClick={() => setSelectedEntity(null)} className="p-3 bg-white text-slate-400 rounded-2xl hover:text-slate-900 transition-all shadow-sm border border-slate-100 active:scale-90"><X size={24} /></button>
                    </div>

                    <div className="flex bg-slate-100 p-1 border-b border-slate-200 shrink-0">
                        {[
                            { id: 'profile', label: 'Identity', icon: Mail },
                            { id: 'history', label: 'Telemetry', icon: HistoryIcon },
                            { id: 'plan', label: 'Study Plan', icon: CalendarDays },
                            { id: 'sessionLog', label: 'First 10M Log', icon: ClipboardList }
                        ].map(t => (
                            <button key={t.id} onClick={() => setDetailTab(t.id as any)} className={`flex-1 py-4 rounded-xl text-[9px] font-black flex items-center justify-center gap-2 transition-all ${detailTab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
                                <t.icon size={14}/> {t.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 overflow-y-auto p-8 md:p-10 custom-scrollbar space-y-10 bg-white">
                        {detailTab === 'profile' && (
                            <div className="space-y-10 animate-fade-in">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-2 shadow-inner group hover:bg-white hover:border-indigo-200 transition-all">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[8px] font-black text-slate-400 tracking-[0.2em]">Readiness</span>
                                            <Target size={12} className="text-indigo-400" />
                                        </div>
                                        <p className="text-4xl font-black text-slate-900 tabular-nums">{getAggregatedScore(selectedEntity.email)}%</p>
                                    </div>
                                    <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-2 shadow-inner group hover:bg-white hover:border-medical-200 transition-all">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[8px] font-black text-slate-400 tracking-[0.2em]">Sessions</span>
                                            <ActivityIcon size={12} className="text-medical-400" />
                                        </div>
                                        <p className="text-4xl font-black text-slate-900 tabular-nums">{selectedEntity.history?.length || 0}</p>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <h4 className="text-[10px] font-black text-slate-400 tracking-[0.2em] border-b border-slate-100 pb-4">Digital Identity Schema</h4>
                                    <div className="space-y-4">
                                        <ProfileDetail label="Wix Identifier" value={selectedEntity.id} icon={Globe} mono />
                                        <ProfileDetail label="Registry Email" value={selectedEntity.email || 'N/A'} icon={Mail} />
                                        <ProfileDetail label="Board Jurisdiction" value={selectedEntity.state || 'Global'} icon={MapPin} />
                                        <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2.5 rounded-xl shadow-xs ${selectedEntity.twoFactorEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-slate-400 border border-slate-200'}`}>
                                                        <ShieldCheck size={18} className={selectedEntity.twoFactorEnabled ? 'text-emerald-600' : 'text-slate-400'} />
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Account 2FA Security</p>
                                                        <p className="text-xs font-black text-slate-900">
                                                            {selectedEntity.twoFactorEnabled ? '2FA Enforced & Active' : '2FA Disabled (Password Only)'}
                                                        </p>
                                                    </div>
                                                </div>
                                                {isSuperAdmin && (
                                                    <button 
                                                        id="drawer-toggle-2fa-btn"
                                                        onClick={() => handleToggleUser2FA(selectedEntity)}
                                                        disabled={toggling2FAUserId === selectedEntity.id}
                                                        className={`px-3.5 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer active:scale-95 flex items-center gap-1.5 border shadow-xs ${
                                                            selectedEntity.twoFactorEnabled
                                                                ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                                                                : 'bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700'
                                                        }`}
                                                    >
                                                        {toggling2FAUserId === selectedEntity.id ? (
                                                            <Loader2 size={12} className="animate-spin" />
                                                        ) : (
                                                            <ShieldCheck size={12} />
                                                        )}
                                                        <span>{selectedEntity.twoFactorEnabled ? 'Turn OFF 2FA' : 'Turn ON 2FA'}</span>
                                                    </button>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-slate-500 leading-relaxed font-medium">
                                                {selectedEntity.twoFactorEnabled 
                                                    ? "Two-Factor Authentication is currently enforced for this account. If this staff member lost their authenticator, Super Admin can turn off 2FA to reset credentials and restore immediate access."
                                                    : "Two-Factor Authentication is currently disabled. Super Admin can turn ON 2FA to require Google Authenticator TOTP verification for this staff account."
                                                }
                                            </p>
                                            {selectedEntity.id === currentUser?.id && (
                                                <div className="pt-2 border-t border-slate-200/60 flex items-center justify-between">
                                                    <span className="text-[9px] font-bold text-slate-400">Current active account:</span>
                                                    <button 
                                                        onClick={() => setShow2FAModal(true)}
                                                        className="text-[10px] font-black text-indigo-600 hover:underline uppercase tracking-wider cursor-pointer flex items-center gap-1"
                                                    >
                                                        <span>Manage My Authenticator & Devices</span> →
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <ProfileDetail label="Service Tier" value={selectedEntity.planTier === 'paid' ? 'Elite Pro' : 'Standard Free'} icon={CreditCard} />
                                        <ProfileDetail label="Creation Phase" value={new Date(selectedEntity.createdAt || '').toLocaleDateString()} icon={CalendarDays} />
                                        {/* Fix: Change RefreshCw to RefreshCcw */}
                                        <ProfileDetail label="Last Logical Sync" value={getRelativeTime(selectedEntity.lastActive)} icon={RefreshCcw} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {detailTab === 'history' && (
                            <div className="space-y-4 animate-fade-in">
                                {selectedEntity.history && selectedEntity.history.length > 0 ? (
                                    selectedEntity.history.map((h, i) => (
                                        <div key={i} className="p-6 bg-slate-50 rounded-3xl border border-slate-100 flex items-center justify-between group hover:bg-white hover:border-indigo-200 transition-all shadow-sm">
                                            <div className="flex items-center gap-5">
                                                <div className={`w-12 h-12 rounded-2xl flex flex-col items-center justify-center font-black shrink-0 shadow-sm border-b-4 ${h.passed ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-rose-50 text-rose-600 border-rose-200'}`}>
                                                    <span className="text-base leading-none tabular-nums">{h.score}%</span>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-black text-slate-800 leading-none">{h.examMode || 'Simulation'}</p>
                                                    <p className="text-[9px] text-slate-400 font-bold tracking-widest mt-1.5">{h.date} • {h.stateCode || 'Global'}</p>
                                                </div>
                                            </div>
                                            <span className={`text-[9px] font-black px-3 py-1 rounded-full ${h.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{h.passed ? 'Qualified' : 'Remediation'}</span>
                                        </div>
                                    ))
                                ) : (
                                    <div className="py-24 text-center grayscale opacity-20 flex flex-col items-center gap-4">
                                        <HistoryIcon size={64} />
                                        <p className="font-black text-xs tracking-widest">Archive Vacant</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {detailTab === 'plan' && (
                            <div className="space-y-8 animate-fade-in">
                                {!selectedEntity.studyPlan?.isInitialized ? (
                                    <div className="py-24 text-center opacity-20 flex flex-col items-center gap-4">
                                        <CalendarDays size={64} />
                                        <p className="font-black text-xs tracking-widest">Strategy Blueprint Not Configured</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden group">
                                            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:rotate-12 transition-transform duration-700"><Target size={120}/></div>
                                            <div className="relative z-10 space-y-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-indigo-500 rounded-xl shadow-lg"><Star size={20}/></div>
                                                    <h4 className="text-xl font-black tracking-tight">Board Target</h4>
                                                </div>
                                                <p className="text-3xl font-black tracking-tighter tabular-nums">{new Date(selectedEntity.studyPlan.targetDate).toLocaleDateString()}</p>
                                                <div className="flex items-center gap-3 pt-2">
                                                    <div className="h-1.5 flex-1 bg-white/10 rounded-full overflow-hidden">
                                                        <div className="h-full bg-indigo-400" style={{ width: `${Math.round((selectedEntity.studyPlan.tasks.filter(t => t.completed).length / selectedEntity.studyPlan.tasks.length) * 100)}%` }}></div>
                                                    </div>
                                                    <span className="text-[10px] font-black">{Math.round((selectedEntity.studyPlan.tasks.filter(t => t.completed).length / selectedEntity.studyPlan.tasks.length) * 100)}% Done</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between px-2">
                                                <span className="text-[10px] font-black text-slate-400 tracking-[0.2em]">Protocol Sequence</span>
                                            </div>
                                            <div className="grid grid-cols-1 gap-2">
                                                {selectedEntity.studyPlan.tasks.map(task => (
                                                    <div key={task.id} className={`p-4 rounded-2xl border flex items-center justify-between group transition-all ${task.completed ? 'bg-emerald-50/50 border-emerald-100 opacity-60' : 'bg-slate-50 border-slate-200'}`}>
                                                        <div className="flex items-center gap-4 min-w-0">
                                                            {task.completed ? <CheckCircle className="text-emerald-500 shrink-0" size={16}/> : <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0"/>}
                                                            <span className={`text-[11px] font-black truncate ${task.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{task.title}</span>
                                                        </div>
                                                        <span className="text-[9px] font-black text-slate-400 whitespace-nowrap bg-white px-2 py-1 rounded-lg border border-slate-100 shadow-sm">{new Date(task.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {detailTab === 'sessionLog' && (
                            <div className="space-y-6 animate-fade-in text-left">
                                <div className="p-4 bg-indigo-50 border border-indigo-100/50 rounded-2xl flex items-start gap-3">
                                    <ClipboardList className="text-indigo-600 shrink-0 mt-0.5" size={16} />
                                    <div>
                                        <h5 className="text-[11px] font-black text-indigo-900 uppercase tracking-wider">Active Cohort Engagement Log</h5>
                                        <p className="text-[10px] text-indigo-700/80 font-medium leading-relaxed mt-0.5">
                                            Sequence of client clicks, assessment steps, and feature actions logged within <strong>the first 10 minutes</strong> of registration to identify interface fatigue or system boundaries.
                                        </p>
                                    </div>
                                </div>

                                {isTraceLoading ? (
                                    <PageLoader message="Harvesting active user journey trace..." compact />
                                ) : (sessionTrace && sessionTrace.timeline && sessionTrace.timeline.length > 0) ? (
                                    <div className="space-y-4">
                                        <div className="relative pl-6 border-l border-dashed border-slate-200 ml-2.5 space-y-5 py-2">
                                            {sessionTrace.timeline.map((ev: any, i: number) => {
                                                const isReg = i === 0;
                                                const stepColor = ev.status === 'success' ? '#10b981' : ev.status === 'warning' ? '#f59e0b' : '#3b82f6';
                                                
                                                return (
                                                    <div key={i} className="relative flex items-start justify-between gap-4">
                                                        <span className="absolute -left-[31px] top-1 w-2.5 h-2.5 rounded-full border border-white" style={{ backgroundColor: stepColor }} />
                                                        
                                                        <div className="space-y-0.5">
                                                            <div className="text-xs font-extrabold text-slate-800 leading-tight flex items-center gap-2">
                                                                {ev.event}
                                                            </div>
                                                            <div className="text-[10px] text-slate-400 font-bold flex items-center gap-1.5">
                                                                <Clock size={10} />
                                                                {new Date(ev.timestamp).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                                {ev.label && <span className="opacity-70">• Label: {ev.label}</span>}
                                                            </div>
                                                        </div>

                                                        <div className="text-right shrink-0">
                                                            <span className="text-[9px] font-black tracking-widest text-indigo-600 font-mono bg-indigo-50/50 border border-indigo-150 px-2 py-0.5 rounded-lg">
                                                                +{ev.relativeTime}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Summarization analysis of friction */}
                                        {(() => {
                                            const hasAnsw = sessionTrace.timeline.some((e: any) => e.action === 'question_answer');
                                            const hasFlip = sessionTrace.timeline.some((e: any) => e.action === 'flashcard_flip');
                                            
                                            // Assess friction behavior
                                            let diagnosticOutcome = "No diagnostic interaction detected.";
                                            if (sessionTrace.timeline.some((e: any) => e.action === 'diagnostic_completed')) {
                                                diagnosticOutcome = "User successfully navigated and completed the diagnostic assessment.";
                                            } else if (sessionTrace.timeline.some((e: any) => e.action?.includes('skipped'))) {
                                                diagnosticOutcome = "User chose to bypass onboarding assessment to enter dashboard directly.";
                                            } else if (sessionTrace.timeline.some((e: any) => e.action === 'diagnostic_started')) {
                                                diagnosticOutcome = "Friction Alert: User started but abandoned onboarding assessment.";
                                            }

                                            return (
                                                <div className="bg-slate-50 border border-slate-100 p-4 rounded-2xl text-[10px] space-y-2 mt-4">
                                                    <span className="font-black text-slate-400 uppercase tracking-widest">UX Confusion Assessment</span>
                                                    <div className="grid grid-cols-2 gap-4 pt-1">
                                                        <div className="space-y-1">
                                                            <p className="font-extrabold text-slate-500">Core Activation State</p>
                                                            <p className={`font-black uppercase tracking-wider text-[9px] ${(hasAnsw || hasFlip) ? 'text-emerald-600' : 'text-rose-500'}`}>
                                                                {(hasAnsw || hasFlip) ? '✔️ Activated (Studied)' : '❌ Unactivated Churn'}
                                                            </p>
                                                        </div>
                                                        <div className="space-y-1">
                                                            <p className="font-extrabold text-slate-500">Onboarding Action</p>
                                                            <p className="font-bold text-slate-700">{diagnosticOutcome}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                ) : (
                                    <div className="py-16 text-center opacity-30 flex flex-col items-center gap-3">
                                        <ClipboardList size={40} className="text-slate-400" />
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">No activities recorded in 10m timeframe</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="p-8 border-t border-slate-100 bg-slate-50/50 grid grid-cols-2 gap-4 shrink-0">
                        {/* Fix: Pass selectedEntity to handleEditUser */}
                        <button 
                            onClick={() => handleEditUser(selectedEntity)}
                            className="py-5 bg-white border border-slate-200 rounded-2xl font-black text-[10px] tracking-widest text-slate-600 hover:text-indigo-600 hover:border-indigo-200 transition-all flex items-center justify-center gap-3 active:scale-[0.98] shadow-sm"
                        >
                            <Edit size={18}/> Manage Registry
                        </button>
                        {/* Fix: Pass selectedEntity to toggleBlockStatus */}
                        <button 
                            onClick={() => toggleBlockStatus(selectedEntity)}
                            className={`py-5 border rounded-2xl font-black text-[10px] tracking-widest transition-all flex items-center justify-center gap-3 active:scale-[0.98] shadow-sm ${selectedEntity.isBlocked ? 'bg-emerald-600 border-emerald-700 text-white hover:bg-emerald-500' : 'bg-rose-50 border-rose-100 text-rose-600 hover:bg-rose-100'}`}
                        >
                            {selectedEntity.isBlocked ? <Unlock size={18}/> : <Ban size={18}/>} 
                            {selectedEntity.isBlocked ? 'Restore Portal' : 'Suspend ID'}
                        </button>
                    </div>
                </div>
            )}

            {/* 2FA & Trusted Device Management Modal */}
            <TwoFactorSecurityModal 
                isOpen={show2FAModal} 
                onClose={() => setShow2FAModal(false)} 
                userEmail={currentUser?.email || ''} 
            />
        </div>
    );
};

const ProfileDetail = ({ label, value, icon: Icon, mono = false }: any) => (
    <div className="flex items-center justify-between p-5 bg-white border border-slate-100 rounded-2xl shadow-sm group hover:border-indigo-100 transition-all">
        <div className="flex items-center gap-4">
            <div className="p-2.5 bg-slate-50 text-slate-400 rounded-xl group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-all shadow-inner"><Icon size={16}/></div>
            <span className="text-[10px] font-black text-slate-400 tracking-widest">{label}</span>
        </div>
        <span className={`text-[11px] font-black text-slate-800 truncate max-w-[200px] text-right ${mono ? 'font-mono tracking-tighter opacity-80' : ''}`}>{value}</span>
    </div>
);

const ToggleLeft = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor" fillOpacity="0.1"/><circle cx="12" cy="12" r="8" fill="white" shadow-lg="true"/></svg>
);

const ToggleRight = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor"/><circle cx="36" cy="12" r="8" fill="white"/></svg>
);